import { jsonBodySerializer } from "@/lib/generated/client";
import { withClient } from "@/lib/api/generated-client";
import type { ErrorResponse as GeneratedErrorResponse } from "@/lib/generated/types.gen";
import {
  DEFAULT_RETRY_AFTER_SECONDS,
  createApiError,
  createErrorFromResponse,
  getRetryAfterSeconds,
  isAccountInactiveError,
  isAuthenticationError,
  isPermissionError,
  isRateLimitError,
} from "@/lib/api-errors";
import { redirectForAuthStateError } from "@/lib/auth/session-redirect";

export {
  isApiError,
  isAccountInactiveError,
  isAuthenticationError,
  isPermissionError,
  isNotFoundError,
  isRateLimitError,
} from "@/lib/api-errors";
export type { ApiError, ApiErrorMetadata } from "@/lib/api-errors";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * Result type from @hey-api SDK calls with throwOnError: false, responseStyle: 'fields'
 * This is a discriminated union where either data or error is present, never both.
 *
 * We use a structural constraint rather than strict generics to accommodate the SDK's
 * complex conditional type unwrapping. Type safety is enforced through the wrapper
 * functions' return type annotations.
 */
type SdkResult = {
  data?: unknown;
  error?: unknown;
  request?: Request;
  response?: Response;
};

function normalizeApiPath(path: string): string {
  if (path.startsWith("/api/v1/")) {
    return path;
  }
  if (path.startsWith("/")) {
    return `/api/v1${path}`;
  }
  return `/api/v1/${path}`;
}

function buildHeaders(method: HttpMethod, headers?: Record<string, string>): Headers {
  const merged = new Headers(headers);
  if (method === "GET" && !merged.has("Cache-Control")) {
    merged.set("Cache-Control", "no-cache");
  }
  return merged;
}

const STATUS_SPECIFIC_MESSAGES: Record<number, string> = {
  401: "Session expired",
  403: "Permission denied",
  404: "Resource not found",
};

/**
 * Handles HTTP errors from API responses
 *
 * This function throws metadata-backed `ApiError` instances. Auth-state errors
 * (401 session expiry, 403 account_inactive) also trigger a hard redirect here,
 * because the backend clears the session cookie on them and any component-level
 * handling would race follow-up 401s. All other navigation is handled by error
 * boundaries or global error handlers.
 *
 * @throws {ApiError} For all non-2xx HTTP responses
 */
async function handleHttpError(
  url: string,
  response: Response,
  errorPayload: unknown,
): Promise<never> {
  const httpStatus = response.status;
  const retryAfterSeconds =
    httpStatus === 429
      ? (getRetryAfterSeconds(response.headers) ?? DEFAULT_RETRY_AFTER_SECONDS)
      : undefined;
  const fallbackMessage = buildFallbackMessage(httpStatus, retryAfterSeconds);
  const error = createErrorFromResponse(response, errorPayload, {
    url,
    retryAfterSeconds,
    fallbackMessage,
  });

  redirectForAuthStateError(error);
  if (isAccountInactiveError(error)) {
    console.warn("[API] Account inactive (403)");
  } else if (isAuthenticationError(error)) {
    console.warn("[API] Session expired (401)");
  } else if (isPermissionError(error)) {
    console.warn("[API] Permission denied (403)");
  } else if (isRateLimitError(error)) {
    console.warn("[API] Rate limited (429)", { retryAfterSeconds });
  }

  throw error;
}

function buildFallbackMessage(status: number, retryAfterSeconds?: number): string {
  if (status === 429) {
    const wait = retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS;
    return `Rate limited. Retry after ${wait}s`;
  }

  return STATUS_SPECIFIC_MESSAGES[status] ?? "Unexpected error";
}

/**
 * Processes an SDK result from @hey-api operations and returns the success payload or throws typed errors.
 *
 * **Return behavior:**
 * - For 2xx responses (except 204/205): Returns the success payload as `TData`
 * - For 204 No Content / 205 Reset Content: Returns `undefined` (cast as `TData` for compatibility)
 *
 * **Error behavior:**
 * - For !ok responses: Throws {@link ApiError} with metadata describing the status.
 *   Use `isAuthenticationError`, `isPermissionError`, etc. to branch on status.
 * - For 2xx with missing data: Throws {@link ApiError} with `details.reason = 'missing_data'`
 *
 * **Usage:**
 * This function is primarily used by generated facade wrappers. Facade functions that wrap
 * 204/205 operations should explicitly return `Promise<undefined>` in their signature.
 *
 * @template TData The expected success payload type
 * @param url The request URL (for error context)
 * @param result The SDK result object with `{ data, error, response, request }`
 * @returns The success payload for 2xx responses, or `undefined` for 204/205
 * @throws {ApiError} On any HTTP error or when a 2xx response lacks data
 */
export async function processResult<TData = unknown>(
  url: string,
  result: SdkResult,
): Promise<TData> {
  const { response, error, data } = result;

  // Handle network errors where no response was received
  if (!response) {
    const networkError = error instanceof Error ? error : new Error("Network request failed");
    throw createApiError(networkError.message, {
      status: 0,
      statusText: "Network Error",
      url,
      details: { reason: "network_error", originalError: String(networkError) },
    });
  }

  if (!response.ok) {
    await handleHttpError(url, response, error);
  }

  if (response.status === 204 || response.status === 205) {
    return undefined as TData;
  }

  if (data === undefined || data === null) {
    throw createApiError("API returned success status but no data", {
      status: response.status,
      statusText: response.statusText || "OK",
      url,
      details: { reason: "missing_data" },
    });
  }

  return data as TData;
}

function isFormData(body: unknown): body is FormData {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

async function performRequest<T, B = unknown>(
  method: HttpMethod,
  endpoint: string,
  body?: B,
  headers?: Record<string, string>,
): Promise<T | undefined> {
  const url = normalizeApiPath(endpoint);

  return withClient(async (client) => {
    const requestHeaders = buildHeaders(method, headers);
    const isBodyAllowed = method !== "GET" && body !== undefined;
    const useFormData = isBodyAllowed && isFormData(body);

    if (isBodyAllowed) {
      if (useFormData) {
        // FormData bodies: Let browser auto-set Content-Type with correct boundary.
        // Explicitly delete to ensure no caller-provided header interferes.
        requestHeaders.delete("Content-Type");
      } else if (!requestHeaders.has("Content-Type")) {
        // JSON bodies: Set Content-Type unless caller provided custom value
        requestHeaders.set("Content-Type", "application/json");
      }
    }

    const result = await client.request<T, GeneratedErrorResponse, false>({
      method,
      url,
      headers: requestHeaders,
      ...(isBodyAllowed ? { body } : {}),
      ...(isBodyAllowed && !useFormData
        ? { bodySerializer: jsonBodySerializer.bodySerializer }
        : {}),
    });

    return processResult<T>(url, result);
  });
}

export async function requestApi<T, B = unknown>(
  method: HttpMethod,
  endpoint: string,
  body?: B,
  headers?: Record<string, string>,
): Promise<T | undefined> {
  return performRequest<T, B>(method, endpoint, body, headers);
}

/**
 * Global fetcher for SWR built on top of the generated client
 */
export async function globalFetcher<T = unknown>(url: string): Promise<T | undefined> {
  try {
    return await requestApi<T>("GET", url);
  } catch (error) {
    console.error("[API] Fetch error:", url, error);
    throw error;
  }
}

/**
 * Legacy export - do not use
 * Use useApiClient hook instead
 */
export async function apiClient(): Promise<never> {
  throw new Error("Use useApiClient hook instead of direct apiClient import");
}
